/**
 * Resham Bazaar product reviews.
 * Preserves the existing Worker API contract while providing an accessible
 * carousel, paginated review modal, and optimized client-side photo uploads.
 */
(function () {
  'use strict';

  const MAX_IMAGES = 3;
  const MAX_SOURCE_BYTES = 12 * 1024 * 1024;
  const MAX_OPTIMIZED_BYTES = 900000;
  const MAX_IMAGE_DIMENSION = 1800;
  const ALL_REVIEWS_PAGE_SIZE = 10;
  const AUTO_SCROLL_DELAY_MS = 3600;
  const DRAWER_CLOSE_DELAY_MS = 1300;

  function initializeAll(scope) {
    const roots = scope.matches?.('[data-rb-reviews-container]')
      ? [scope]
      : Array.from(scope.querySelectorAll?.('[data-rb-reviews-container]') || []);
    roots.forEach((root) => {
      if (root.dataset.rbReviewsInitialized === 'true') return;
      root.dataset.rbReviewsInitialized = 'true';
      initializeReviews(root);
    });
  }

  function initializeReviews(root) {
    const baseUrl = String(root.dataset.proxyUrl || '').replace(/\/$/, '');
    const productId = String(root.dataset.productId || '');
    const perPage = clampInteger(root.dataset.reviewsPerPage, 6, 3, 12);
    if (!baseUrl || !productId) {
      console.error('RB Reviews: missing Worker URL or Shopify product ID.');
      return;
    }

    const elements = {
      average: root.querySelector('[data-rb-average-rating]'),
      stars: root.querySelector('[data-rb-average-stars]'),
      total: root.querySelector('[data-rb-total-reviews]'),
      list: root.querySelector('[data-rb-reviews-list]'),
      carousel: root.querySelector('[data-rb-reviews-carousel]'),
      carouselPrevious: root.querySelector('[data-rb-carousel-prev]'),
      carouselNext: root.querySelector('[data-rb-carousel-next]'),
      carouselStatus: root.querySelector('[data-rb-carousel-status]'),
      carouselControls: root.querySelector('.rb-reviews-carousel__controls'),
      empty: root.querySelector('[data-rb-reviews-empty]'),
      loading: root.querySelector('#rb-reviews-loading-state'),
      sort: root.querySelector('#rb-reviews-sort-select'),
      readAllWrap: root.querySelector('[data-rb-read-all-wrap]'),
      readAllButton: root.querySelector('[data-rb-read-all]'),
      gallery: root.querySelector('[data-rb-review-gallery]'),
      galleryCount: root.querySelector('[data-rb-gallery-count]'),
      galleryEmpty: root.querySelector('[data-rb-gallery-empty]'),
      panel: root.querySelector('[data-rb-review-form-panel]'),
      form: root.querySelector('[data-rb-review-form]'),
      message: root.querySelector('[data-rb-review-form-message]'),
      fileInput: root.querySelector('[data-rb-review-images]'),
      previews: root.querySelector('[data-rb-review-previews]'),
      allModal: root.querySelector('[data-rb-all-reviews-modal]'),
      allPanel: root.querySelector('.rb-all-reviews-modal__panel'),
      allList: root.querySelector('[data-rb-all-reviews-list]'),
      allLoading: root.querySelector('[data-rb-all-reviews-loading]'),
      allEmpty: root.querySelector('[data-rb-all-reviews-empty]'),
      allPagination: root.querySelector('[data-rb-all-reviews-pagination]'),
      allPrevious: root.querySelector('[data-rb-all-reviews-prev]'),
      allNext: root.querySelector('[data-rb-all-reviews-next]'),
      allPages: root.querySelector('[data-rb-all-reviews-pages]'),
      allCount: root.querySelector('[data-rb-all-reviews-count]'),
    };

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const state = {
      total: 0,
      sort: 'newest',
      images: [],
      loading: false,
      closeTimer: 0,
      reviewTrigger: null,
      allTrigger: null,
      carousel: {
        reviews: [],
        enabled: false,
        setWidth: 0,
        step: 0,
        timer: 0,
        scrollTimer: 0,
        hovered: false,
        focused: false,
        dragPending: false,
        dragging: false,
        dragPointerId: null,
        dragStartX: 0,
        dragStartScroll: 0,
      },
      all: { page: 1, total: 0, loading: false },
    };

    const endpoint = (path, params) => {
      const url = new URL(path, `${baseUrl}/`);
      Object.entries(params || {}).forEach(([key, value]) => url.searchParams.set(key, value));
      return url.toString();
    };

    async function fetchJson(url, options) {
      const response = await fetch(url, { cache: 'no-store', ...(options || {}) });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload.success === false) {
        throw new Error(payload.message || 'The review service is temporarily unavailable.');
      }
      return payload;
    }

    async function loadSummary() {
      try {
        const summary = await fetchJson(endpoint('/api/review-summary', { product_id: productId }));
        const total = Number(summary.total_reviews || 0);
        const average = Number(summary.average_rating || 0);
        if (elements.average) elements.average.textContent = average.toFixed(1);
        if (elements.stars) {
          renderStars(elements.stars, average);
          elements.stars.setAttribute('aria-label', `${average.toFixed(1)} out of 5 stars`);
        }
        if (elements.total) elements.total.textContent = String(total);
        for (let rating = 1; rating <= 5; rating += 1) {
          const count = Number(summary.star_breakdown?.[rating] || 0);
          const percentage = total ? Math.round((count / total) * 100) : 0;
          const bar = root.querySelector(`[data-rb-rating-bar="${rating}"]`);
          const label = root.querySelector(`[data-rb-rating-count="${rating}"]`);
          if (bar) bar.style.width = `${percentage}%`;
          if (label) label.textContent = String(count);
        }
      } catch (error) {
        console.error('RB Reviews: summary request failed.', error);
      }
    }

    async function loadReviews() {
      if (state.loading) return;
      state.loading = true;
      setListLoading(true);
      try {
        const data = await fetchJson(endpoint('/api/reviews', {
          product_id: productId,
          page: 1,
          limit: Math.max(perPage, 6),
          sort: state.sort,
        }));
        state.total = Number(data.total_count || 0);
        renderReviews(Array.isArray(data.reviews) ? data.reviews : []);
      } catch (error) {
        renderListError(error.message);
      } finally {
        state.loading = false;
        setListLoading(false);
      }
    }

    async function loadGallery() {
      if (!elements.gallery) return;
      try {
        const data = await fetchJson(endpoint('/api/reviews', {
          product_id: productId,
          page: 1,
          limit: 12,
          sort: 'newest',
          photos_only: 'true',
        }));
        const photos = [];
        (data.reviews || []).forEach((review) => {
          (review.images || []).forEach((url) => photos.push({ url, name: review.name }));
        });
        renderGallery(photos.slice(0, 12));
      } catch (error) {
        console.error('RB Reviews: gallery request failed.', error);
        renderGallery([]);
      }
    }

    function renderReviews(reviews) {
      if (!elements.list || !elements.carousel) return;
      stopCarousel();
      elements.list.replaceChildren();
      state.carousel.reviews = reviews;
      const hasReviews = reviews.length > 0;
      const carouselEnabled = reviews.length > 3;
      state.carousel.enabled = carouselEnabled;
      elements.list.classList.toggle('is-static', !carouselEnabled);
      elements.list.classList.remove('review-count-1', 'review-count-2', 'review-count-3');
      if (!carouselEnabled && hasReviews) elements.list.classList.add(`review-count-${reviews.length}`);
      if (elements.carouselControls) elements.carouselControls.hidden = !carouselEnabled;
      elements.carousel.style.display = hasReviews ? '' : 'none';
      if (elements.empty) elements.empty.style.display = hasReviews ? 'none' : 'block';
      if (elements.readAllWrap) elements.readAllWrap.style.display = hasReviews ? '' : 'none';
      if (!hasReviews) return;

      const repeatCount = carouselEnabled ? 3 : 1;
      for (let repetition = 0; repetition < repeatCount; repetition += 1) {
        reviews.forEach((review) => {
          const duplicate = carouselEnabled && repetition !== 1;
          elements.list.append(createReviewCard(review, { compact: true, duplicate }));
        });
      }

      requestAnimationFrame(() => {
        if (carouselEnabled) {
          measureCarousel(true);
          updateCarouselStatus();
          scheduleCarousel();
        }
      });
    }

    function measureCarousel(reset = false) {
      if (!elements.list || !state.carousel.enabled || !state.carousel.reviews.length) return;
      const cards = elements.list.querySelectorAll('.rb-review-item');
      const first = cards[0];
      const second = cards[1];
      if (!first) return;
      const previousStep = state.carousel.step || 1;
      const previousOrigin = state.carousel.setWidth || 0;
      const previousIndex = reset
        ? 0
        : Math.round((elements.list.scrollLeft - previousOrigin) / previousStep);
      state.carousel.step = second ? second.offsetLeft - first.offsetLeft : first.offsetWidth;
      state.carousel.setWidth = cards.length > state.carousel.reviews.length
        ? cards[state.carousel.reviews.length].offsetLeft - first.offsetLeft
        : 0;
      elements.list.style.scrollBehavior = 'auto';
      elements.list.scrollLeft = state.carousel.setWidth + (previousIndex * state.carousel.step);
      requestAnimationFrame(() => elements.list.style.removeProperty('scroll-behavior'));
    }

    function createReviewCard(review, options = {}) {
      const card = node('article', `rb-review-item${options.compact ? ' rb-review-item--compact' : ' rb-review-item--full'}`);
      card.setAttribute('role', 'listitem');
      if (options.duplicate) card.setAttribute('aria-hidden', 'true');

      const header = node('div', 'rb-review-item__header');
      const avatar = node('div', 'rb-review-item__avatar', initial(review.name));
      avatar.setAttribute('aria-hidden', 'true');
      const identity = node('div', 'rb-review-item__identity');
      identity.append(node('h4', 'rb-review-item__name', review.name || 'Customer'));
      const metaParts = [formatDate(review.date), review.location].filter(Boolean);
      identity.append(node('p', 'rb-review-item__meta', metaParts.join(' · ')));
      const stars = createStars(review.rating, 'rb-review-item__stars');
      stars.setAttribute('aria-label', `${review.rating} out of 5 stars`);
      identity.append(stars);
      header.append(avatar, identity);

      card.append(header);
      card.append(node('h5', 'rb-review-item__title', review.title || 'Customer review'));
      card.append(node('p', 'rb-review-item__body', review.text || ''));

      if (Array.isArray(review.images) && review.images.length) {
        const photos = node('div', 'rb-review-item__photos');
        review.images.forEach((url) => photos.append(createPhotoButton(url, review.name, options.duplicate)));
        card.append(photos);
      }

      const badgeText = review.verified_purchase ? 'Verified Purchase' : (review.badge || 'Customer Review');
      const badge = node('span', `rb-review-item__badge${review.verified_purchase ? ' is-verified' : ''}`, badgeText);
      card.append(badge);
      return card;
    }

    function renderGallery(photos) {
      if (!elements.gallery) return;
      elements.gallery.replaceChildren();
      photos.forEach((photo) => {
        const button = node('button', 'rb-review-gallery__item');
        button.type = 'button';
        button.setAttribute('aria-label', `View photo shared by ${photo.name || 'a customer'}`);
        const image = document.createElement('img');
        image.src = photo.url;
        image.alt = `Customer review photo shared by ${photo.name || 'a customer'}`;
        image.loading = 'lazy';
        image.decoding = 'async';
        image.width = 320;
        image.height = 358;
        button.append(image, node('span', '', photo.name || 'Customer'));
        button.addEventListener('click', () => openPhoto(photo.url, image.alt));
        elements.gallery.append(button);
      });
      if (elements.galleryCount) elements.galleryCount.textContent = `${photos.length} photo${photos.length === 1 ? '' : 's'}`;
      if (elements.galleryEmpty) elements.galleryEmpty.style.display = photos.length ? 'none' : 'block';
      elements.gallery.style.display = photos.length ? 'grid' : 'none';
    }

    function createPhotoButton(url, author, duplicate = false) {
      const button = node('button', 'rb-review-item__photo');
      button.type = 'button';
      button.setAttribute('aria-label', `View photo shared by ${author || 'a customer'}`);
      if (duplicate) button.tabIndex = -1;
      const image = document.createElement('img');
      image.src = url;
      image.alt = `Customer review photo shared by ${author || 'a customer'}`;
      image.loading = 'lazy';
      image.decoding = 'async';
      image.width = 144;
      image.height = 160;
      button.append(image);
      button.addEventListener('click', () => openPhoto(url, image.alt));
      return button;
    }

    function scheduleCarousel() {
      stopCarouselTimer();
      if (!state.carousel.enabled || reducedMotion.matches || isCarouselPaused()) return;
      state.carousel.timer = window.setTimeout(() => {
        moveCarousel(1);
        scheduleCarousel();
      }, AUTO_SCROLL_DELAY_MS);
    }

    function stopCarouselTimer() {
      if (state.carousel.timer) window.clearTimeout(state.carousel.timer);
      state.carousel.timer = 0;
    }

    function stopCarousel() {
      stopCarouselTimer();
      if (state.carousel.scrollTimer) window.clearTimeout(state.carousel.scrollTimer);
      state.carousel.scrollTimer = 0;
    }

    function isCarouselPaused() {
      return state.carousel.hovered || state.carousel.focused || state.carousel.dragging || document.hidden;
    }

    function moveCarousel(direction) {
      if (!elements.list || !state.carousel.enabled || !state.carousel.step) return;
      elements.list.scrollBy({ left: state.carousel.step * direction, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }

    function normalizeCarousel() {
      if (!elements.list || !state.carousel.enabled || !state.carousel.setWidth || state.carousel.dragging) return;
      const width = state.carousel.setWidth;
      if (elements.list.scrollLeft < width * 0.5) elements.list.scrollLeft += width;
      else if (elements.list.scrollLeft > width * 1.5) elements.list.scrollLeft -= width;
      updateCarouselStatus();
    }

    function updateCarouselStatus() {
      if (!elements.carouselStatus || !state.carousel.reviews.length) return;
      const step = state.carousel.step || 1;
      const origin = state.carousel.setWidth || 0;
      const raw = Math.round((elements.list.scrollLeft - origin) / step);
      const current = ((raw % state.carousel.reviews.length) + state.carousel.reviews.length) % state.carousel.reviews.length + 1;
      elements.carouselStatus.textContent = `Review ${current} of ${state.carousel.reviews.length}`;
    }

    function onCarouselScroll() {
      if (state.carousel.scrollTimer) window.clearTimeout(state.carousel.scrollTimer);
      state.carousel.scrollTimer = window.setTimeout(normalizeCarousel, 130);
    }

    function startMouseDrag(event) {
      if (!state.carousel.enabled || event.pointerType !== 'mouse' || event.button !== 0 || !elements.list) return;
      if (event.target.closest('button, a')) return;
      state.carousel.dragPending = true;
      state.carousel.dragPointerId = event.pointerId;
      state.carousel.dragStartX = event.clientX;
      state.carousel.dragStartScroll = elements.list.scrollLeft;
    }

    function continueMouseDrag(event) {
      if ((!state.carousel.dragPending && !state.carousel.dragging) || !elements.list) return;
      const distance = event.clientX - state.carousel.dragStartX;
      if (!state.carousel.dragging && Math.abs(distance) < 6) return;
      if (!state.carousel.dragging) {
        state.carousel.dragging = true;
        elements.list.classList.add('is-dragging');
        elements.list.setPointerCapture?.(state.carousel.dragPointerId);
        stopCarouselTimer();
      }
      event.preventDefault();
      elements.list.scrollLeft = state.carousel.dragStartScroll - distance;
    }

    function endMouseDrag(event) {
      if ((!state.carousel.dragPending && !state.carousel.dragging) || !elements.list) return;
      const wasDragging = state.carousel.dragging;
      state.carousel.dragPending = false;
      state.carousel.dragging = false;
      elements.list.classList.remove('is-dragging');
      if (elements.list.hasPointerCapture?.(state.carousel.dragPointerId)) elements.list.releasePointerCapture(state.carousel.dragPointerId);
      state.carousel.dragPointerId = null;
      if (wasDragging) normalizeCarousel();
      scheduleCarousel();
    }

    async function openAllReviews(event) {
      if (!elements.allModal || !elements.allPanel) return;
      state.allTrigger = event?.currentTarget || document.activeElement;
      state.all.page = 1;
      elements.allModal.classList.add('is-open');
      elements.allModal.removeAttribute('inert');
      elements.allModal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('rb-review-modal-open');
      elements.allPanel.focus({ preventScroll: true });
      await loadAllReviews();
    }

    function closeAllReviews() {
      if (!elements.allModal) return;
      elements.allModal.classList.remove('is-open');
      elements.allModal.setAttribute('aria-hidden', 'true');
      elements.allModal.setAttribute('inert', '');
      if (!elements.panel?.classList.contains('is-open')) document.body.classList.remove('rb-review-modal-open');
      state.allTrigger?.focus?.({ preventScroll: true });
    }

    async function loadAllReviews() {
      if (state.all.loading || !elements.allList) return;
      state.all.loading = true;
      if (elements.allLoading) elements.allLoading.hidden = false;
      elements.allList.replaceChildren();
      if (elements.allEmpty) elements.allEmpty.hidden = true;
      try {
        const data = await fetchJson(endpoint('/api/reviews', {
          product_id: productId,
          page: state.all.page,
          limit: ALL_REVIEWS_PAGE_SIZE,
          sort: state.sort,
        }));
        const reviews = Array.isArray(data.reviews) ? data.reviews : [];
        state.all.total = Number(data.total_count || 0);
        reviews.forEach((review) => elements.allList.append(createReviewCard(review, { compact: false })));
        if (elements.allEmpty) elements.allEmpty.hidden = reviews.length !== 0;
        if (elements.allCount) {
          elements.allCount.textContent = `${state.all.total} published customer review${state.all.total === 1 ? '' : 's'}`;
        }
        renderAllPagination();
      } catch (error) {
        const message = node('p', 'rb-reviews-error', error.message);
        message.setAttribute('role', 'alert');
        elements.allList.append(message);
        if (elements.allPagination) elements.allPagination.hidden = true;
      } finally {
        state.all.loading = false;
        if (elements.allLoading) elements.allLoading.hidden = true;
      }
    }

    function renderAllPagination() {
      if (!elements.allPagination || !elements.allPages) return;
      const pageCount = Math.max(1, Math.ceil(state.all.total / ALL_REVIEWS_PAGE_SIZE));
      elements.allPagination.hidden = pageCount <= 1;
      if (elements.allPrevious) elements.allPrevious.disabled = state.all.page <= 1;
      if (elements.allNext) elements.allNext.disabled = state.all.page >= pageCount;
      elements.allPages.replaceChildren();
      paginationItems(state.all.page, pageCount).forEach((item) => {
        if (item === 'ellipsis') {
          const ellipsis = node('span', 'rb-all-reviews-pagination__ellipsis', '…');
          ellipsis.setAttribute('aria-hidden', 'true');
          elements.allPages.append(ellipsis);
          return;
        }
        const button = node('button', item === state.all.page ? 'is-current' : '', item);
        button.type = 'button';
        button.setAttribute('aria-label', `Go to review page ${item}`);
        if (item === state.all.page) button.setAttribute('aria-current', 'page');
        button.addEventListener('click', () => goToAllPage(item));
        elements.allPages.append(button);
      });
    }

    function paginationItems(current, total) {
      if (total <= 7) return Array.from({ length: total }, (_, index) => index + 1);
      const items = [1];
      const start = Math.max(2, current - 1);
      const end = Math.min(total - 1, current + 1);
      if (start > 2) items.push('ellipsis');
      for (let page = start; page <= end; page += 1) items.push(page);
      if (end < total - 1) items.push('ellipsis');
      items.push(total);
      return items;
    }

    function goToAllPage(page) {
      const pageCount = Math.max(1, Math.ceil(state.all.total / ALL_REVIEWS_PAGE_SIZE));
      const nextPage = clampInteger(page, state.all.page, 1, pageCount);
      if (nextPage === state.all.page || state.all.loading) return;
      state.all.page = nextPage;
      elements.allPanel?.scrollTo({ top: 0, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      loadAllReviews();
    }

    function setListLoading(isLoading) {
      if (elements.loading) elements.loading.style.display = isLoading ? 'flex' : 'none';
      if (isLoading && elements.carousel) elements.carousel.style.display = 'none';
    }

    function renderListError(message) {
      if (!elements.list || !elements.carousel) return;
      stopCarousel();
      const error = node('p', 'rb-reviews-error', message || 'Reviews could not be loaded. Please try again.');
      error.setAttribute('role', 'alert');
      elements.list.replaceChildren(error);
      elements.carousel.style.display = '';
      if (elements.empty) elements.empty.style.display = 'none';
      if (elements.readAllWrap) elements.readAllWrap.style.display = 'none';
    }

    function openPanel(event) {
      if (!elements.panel) return;
      if (state.closeTimer) window.clearTimeout(state.closeTimer);
      state.reviewTrigger = event?.currentTarget || document.activeElement;
      clearMessage();
      elements.panel.classList.add('is-open');
      elements.panel.removeAttribute('inert');
      elements.panel.setAttribute('aria-hidden', 'false');
      document.body.classList.add('rb-review-modal-open');
      elements.form?.querySelector('button, input, textarea')?.focus();
    }

    function closePanel() {
      elements.panel?.classList.remove('is-open');
      elements.panel?.setAttribute('aria-hidden', 'true');
      elements.panel?.setAttribute('inert', '');
      if (!elements.allModal?.classList.contains('is-open')) document.body.classList.remove('rb-review-modal-open');
      state.reviewTrigger?.focus?.({ preventScroll: true });
    }

    function setRating(rating) {
      const value = clampInteger(rating, 5, 1, 5);
      const input = root.querySelector('[data-rb-rating-value]');
      if (input) input.value = String(value);
      root.querySelectorAll('[data-rb-rating-input] button').forEach((button) => {
        const active = Number(button.dataset.rating) <= value;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    async function selectImages(fileList) {
      clearMessage();
      const files = Array.from(fileList || []);
      if (state.images.length + files.length > MAX_IMAGES) {
        showMessage(`You can upload up to ${MAX_IMAGES} photos.`, true);
        return;
      }
      for (const file of files) {
        if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
          showMessage('Please choose JPEG, PNG, or WebP photos.', true);
          continue;
        }
        if (file.size > MAX_SOURCE_BYTES) {
          showMessage('Each original photo must be smaller than 12 MB.', true);
          continue;
        }
        try {
          showMessage('Optimizing your photo…');
          state.images.push(await optimizeImage(file));
          renderPreviews();
          clearMessage();
        } catch (error) {
          showMessage(error.message || 'That photo could not be processed.', true);
        }
      }
      if (elements.fileInput) elements.fileInput.value = '';
    }

    function renderPreviews() {
      if (!elements.previews) return;
      elements.previews.replaceChildren();
      state.images.forEach((url, index) => {
        const wrapper = node('div', 'rb-review-form__preview');
        const image = document.createElement('img');
        image.src = url;
        image.alt = `Selected review photo ${index + 1}`;
        const remove = node('button', 'rb-review-form__preview-remove', '×');
        remove.type = 'button';
        remove.setAttribute('aria-label', `Remove selected photo ${index + 1}`);
        remove.addEventListener('click', () => {
          state.images.splice(index, 1);
          renderPreviews();
        });
        wrapper.append(image, remove);
        elements.previews.append(wrapper);
      });
    }

    async function submitReview(event) {
      event.preventDefault();
      if (!elements.form) return;
      clearMessage();
      const submit = elements.form.querySelector('[type="submit"]');
      const originalLabel = submit?.textContent;
      if (submit) { submit.disabled = true; submit.textContent = 'Submitting…'; }
      const form = new FormData(elements.form);
      const payload = {
        product_id: productId,
        product_handle: root.dataset.productHandle || form.get('product_handle'),
        product_title: root.dataset.productTitle || form.get('product_title'),
        name: form.get('name'),
        email: form.get('email'),
        rating: Number(form.get('rating')),
        title: form.get('title'),
        body: form.get('body'),
        location: form.get('location') || '',
        images: state.images,
      };
      try {
        const result = await fetchJson(endpoint('/api/reviews'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        showMessage(result.message || 'Thank you. Your review was submitted successfully.', false, true);
        elements.form.reset();
        state.images = [];
        renderPreviews();
        setRating(5);
        if (result.status === 'approved') {
          await Promise.allSettled([loadSummary(), loadReviews(), loadGallery()]);
        }
        state.closeTimer = window.setTimeout(() => {
          closePanel();
          window.setTimeout(clearMessage, 350);
        }, DRAWER_CLOSE_DELAY_MS);
      } catch (error) {
        showMessage(error.message, true);
      } finally {
        if (submit) { submit.disabled = false; submit.textContent = originalLabel; }
      }
    }

    function showMessage(message, isError = false, isSuccess = false) {
      if (!elements.message) return;
      elements.message.textContent = message || '';
      elements.message.classList.toggle('is-error', Boolean(isError));
      elements.message.classList.toggle('is-success', Boolean(isSuccess));
      elements.message.setAttribute('role', isError ? 'alert' : 'status');
    }

    function clearMessage() { showMessage('', false, false); }

    root.querySelectorAll('[data-rb-toggle-review-form]').forEach((button) => button.addEventListener('click', openPanel));
    root.querySelectorAll('[data-rb-close-review-form]').forEach((button) => button.addEventListener('click', closePanel));
    root.querySelectorAll('[data-rb-close-all-reviews]').forEach((button) => button.addEventListener('click', closeAllReviews));
    root.querySelectorAll('[data-rb-rating-input] button').forEach((button) => {
      button.addEventListener('click', () => setRating(button.dataset.rating));
    });
    elements.sort?.addEventListener('change', () => {
      state.sort = elements.sort.value;
      loadReviews();
      if (elements.allModal?.classList.contains('is-open')) {
        state.all.page = 1;
        loadAllReviews();
      }
    });
    elements.fileInput?.addEventListener('change', (event) => selectImages(event.target.files));
    elements.form?.addEventListener('submit', submitReview);
    elements.readAllButton?.addEventListener('click', openAllReviews);
    elements.allPrevious?.addEventListener('click', () => goToAllPage(state.all.page - 1));
    elements.allNext?.addEventListener('click', () => goToAllPage(state.all.page + 1));
    elements.carouselPrevious?.addEventListener('click', () => { moveCarousel(-1); scheduleCarousel(); });
    elements.carouselNext?.addEventListener('click', () => { moveCarousel(1); scheduleCarousel(); });
    elements.list?.addEventListener('scroll', onCarouselScroll, { passive: true });
    elements.list?.addEventListener('pointerdown', startMouseDrag);
    elements.list?.addEventListener('pointermove', continueMouseDrag);
    elements.list?.addEventListener('pointerup', endMouseDrag);
    elements.list?.addEventListener('pointercancel', endMouseDrag);
    elements.carousel?.addEventListener('mouseenter', () => { state.carousel.hovered = true; stopCarouselTimer(); });
    elements.carousel?.addEventListener('mouseleave', () => { state.carousel.hovered = false; scheduleCarousel(); });
    elements.carousel?.addEventListener('focusin', () => { state.carousel.focused = true; stopCarouselTimer(); });
    elements.carousel?.addEventListener('focusout', (event) => {
      if (!elements.carousel.contains(event.relatedTarget)) {
        state.carousel.focused = false;
        scheduleCarousel();
      }
    });
    reducedMotion.addEventListener?.('change', scheduleCarousel);
    if ('ResizeObserver' in window && elements.list) {
      const carouselObserver = new ResizeObserver(() => requestAnimationFrame(() => {
        measureCarousel(false);
        normalizeCarousel();
      }));
      carouselObserver.observe(elements.list);
    }
    document.addEventListener('visibilitychange', scheduleCarousel);
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && elements.allModal?.classList.contains('is-open')) {
        closeAllReviews();
      } else if (event.key === 'Escape' && elements.panel?.classList.contains('is-open')) {
        closePanel();
      } else if (event.key === 'Tab' && elements.allModal?.classList.contains('is-open')) {
        trapFocus(event, elements.allPanel);
      } else if (event.key === 'Tab' && elements.panel?.classList.contains('is-open')) {
        trapFocus(event, elements.panel.querySelector('.rb-review-form'));
      }
    });

    setRating(5);
    Promise.allSettled([loadSummary(), loadReviews(), loadGallery()]);
  }

  async function optimizeImage(file) {
    const source = await loadImage(file);
    let width = source.width;
    let height = source.height;
    const scale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(width, height));
    width = Math.max(1, Math.round(width * scale));
    height = Math.max(1, Math.round(height * scale));
    let quality = 0.86;
    for (let attempt = 0; attempt < 10; attempt += 1) {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d', { alpha: false });
      if (!context) throw new Error('Image optimization is unavailable in this browser.');
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, width, height);
      context.drawImage(source.image, 0, 0, width, height);
      let blob = await canvasToBlob(canvas, 'image/webp', quality);
      if (!blob || blob.type !== 'image/webp') blob = await canvasToBlob(canvas, 'image/jpeg', quality);
      if (blob && blob.size <= MAX_OPTIMIZED_BYTES) {
        source.close?.();
        return blobToDataUrl(blob);
      }
      if (quality > 0.7) quality -= 0.06;
      else {
        width = Math.max(1, Math.round(width * 0.85));
        height = Math.max(1, Math.round(height * 0.85));
      }
    }
    source.close?.();
    throw new Error('This photo could not be reduced below 900 KB. Please choose a smaller photo.');
  }

  async function loadImage(file) {
    if ('createImageBitmap' in window) {
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
      return { image: bitmap, width: bitmap.width, height: bitmap.height, close: () => bitmap.close() };
    }
    const url = URL.createObjectURL(file);
    try {
      const image = new Image();
      image.decoding = 'async';
      image.src = url;
      await image.decode();
      return { image, width: image.naturalWidth, height: image.naturalHeight };
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  function canvasToBlob(canvas, type, quality) {
    return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
  }

  function blobToDataUrl(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('The optimized photo could not be read.'));
      reader.readAsDataURL(blob);
    });
  }

  function openPhoto(url, alt) {
    const dialog = node('dialog', 'rb-review-lightbox');
    const close = node('button', 'rb-review-lightbox__close', '×');
    close.type = 'button';
    close.setAttribute('aria-label', 'Close photo');
    const image = document.createElement('img');
    image.src = url;
    image.alt = alt;
    dialog.append(close, image);
    document.body.append(dialog);
    close.addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });
    dialog.addEventListener('close', () => dialog.remove(), { once: true });
    dialog.showModal();
  }

  function createStars(rating, className) {
    const wrapper = node('span', `rb-stars${className ? ` ${className}` : ''}`);
    renderStars(wrapper, rating);
    return wrapper;
  }

  function renderStars(wrapper, rating) {
    const rounded = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
    wrapper.classList.add('rb-stars');
    wrapper.replaceChildren();
    for (let index = 1; index <= 5; index += 1) {
      const star = node('span', index <= rounded ? 'is-filled' : 'is-empty', '★');
      star.setAttribute('aria-hidden', 'true');
      wrapper.append(star);
    }
  }

  function trapFocus(event, container) {
    if (!container) return;
    const focusable = Array.from(container.querySelectorAll('button:not([disabled]),a[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'));
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function node(tag, className, text) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = String(text);
    return element;
  }

  function initial(name) { return String(name || 'C').trim().charAt(0).toUpperCase() || 'C'; }

  function formatDate(value) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '' : new Intl.DateTimeFormat('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric',
    }).format(date);
  }

  function clampInteger(value, fallback, minimum, maximum) {
    const number = Number(value);
    return Number.isInteger(number) ? Math.min(maximum, Math.max(minimum, number)) : fallback;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(document));
  } else {
    initializeAll(document);
  }
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
